<!-- https://www.figma.com/file/reAk2sU4YpRajpQu2YBOHk/007%2F1.-%D0%AD%D0%BB%D0%B5%D0%BC%D0%B5%D0%BD%D1%82%D1%8B?type=design&node-id=167-14540&t=Sf11d7jBSxY9MSdr-0 -->
<template>
  <AppboxError v-if="attributes.fallback" />
  <div
    v-else
    class="appbox"
  >
    <a
      v-if="attributes.url"
      class="appbox__link"
      :href="attributes.url"
      target="_blank"
    />
    <div class="appbox__body">
      <div
        v-if="attributes.iconUrl"
        class="appbox__picture"
      >
        <img
          class="appbox__picture-img"
          :src="attributes.iconUrl"
        />
      </div>
      <div class="appbox__app-info">
        <div class="appbox__app-name-wrapper">
          <span class="appbox__app-name">{{ attributes.name }}</span>
          <ClientOnly>
            <SvgAsset
              v-if="isEditor"
              class="appbox__reload-icon"
              :class="{ 'appbox__reload-icon&#45;&#45;loading': isLoadingNewAttributes }"
              :content="restoreIconContent"
              @click="reloadAppbox"
            />
          </ClientOnly>
        </div>

        <div class="appbox__app-info-additional">
          <span class="appbox__author-name">{{ attributes.developer }}</span>
          <img
            v-if="icon"
            class="appbox__store-icon"
            :src="icon"
          />
          <span class="appbox__price">Цена: {{ attributes.price }}</span>
        </div>
      </div>
    </div>
    <a
      class="appbox__arrow-wrapper"
      :href="attributes.url"
      target="_blank"
    >
      <SvgAsset
        class="appbox__arrow"
        :content="toIconContent"
      />
    </a>
    <a
      class="appbox__button"
      :href="attributes.url"
      target="_blank"
    >
      <img
        v-if="icon"
        class="appbox__button-icon"
        :src="icon"
      />
      <span class="appbox__button-text">Загрузить</span>
    </a>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import AppboxError from '../AppboxError.vue';
  import type { AppBoxAttributes } from '~/types/components/AppBox';
  import { useIndexStore } from '~/store';
  import { usePageStore } from '~/store/pagesData';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import restoreIconContent from '~/assets/images/restore.svg?raw';
  import toIconContent from '~/assets/images/to.svg?raw';
  import appstoreIcon from '@/assets/images/appbox/appstore.png';
  import chromewebstoreIcon from '@/assets/images/appbox/chromewebstore.png';
  import firefoxaddonsIcon from '@/assets/images/appbox/firefoxaddons.png';
  import windowsstoreIcon from '@/assets/images/appbox/windowsstore.png';
  import steamIcon from '@/assets/images/appbox/steam.png';
  import operaaddonsIcon from '@/assets/images/appbox/operaaddons.png';
  import googleplayappsIcon from '@/assets/images/appbox/googleplayapps.png';

  export default defineNuxtComponent({
    name: 'AppBox',

    components: {
      AppboxError,
      SvgAsset,
    },

    props: {
      elem: {
        type: Object as PropType<TContent<AppBoxAttributes>>,
        required: true,
      },
    },

    data() {
      return {
        newAttributes: null as null | AppBoxAttributes,
        isLoadingNewAttributes: false as boolean,
      };
    },

    setup() {
      return {
        indexStore: useIndexStore(),
        pageStore: usePageStore(),
      };
    },

    computed: {
      restoreIconContent() {
        return restoreIconContent;
      },
      toIconContent() {
        return toIconContent;
      },
      attributes(): AppBoxAttributes {
        return this.newAttributes ?? this.elem.attributes;
      },

      isEditor(): boolean {
        return this.indexStore.isAuthenticatedInWordpress;
      },

      icon() {
        switch (this.attributes.platform) {
          case 'appstore':
            return appstoreIcon;
          case 'chromewebstore':
            return chromewebstoreIcon;
          case 'firefoxaddons':
            return firefoxaddonsIcon;
          case 'windowsstore':
            return windowsstoreIcon;
          case 'steam':
            return steamIcon;
          case 'operaaddons':
            return operaaddonsIcon;
          case 'googleplayapps':
            return googleplayappsIcon;
        }
      },
    },

    methods: {
      async reloadAppbox() {
        const articleId = this.pageStore.currentArticle?.id;
        const appUrl = this.attributes.url;

        if (!articleId) {
          return;
        }

        try {
          this.isLoadingNewAttributes = true;
          const {
            data: { data: newAttributes },
          } = await this.$wordpressApi.fetchAppBoxData(articleId, appUrl ?? '');
          this.newAttributes = newAttributes;
        } catch {
        } finally {
          this.isLoadingNewAttributes = false;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .appbox {
    display: flex;
    align-items: center;
    border: 2px solid #e7e7e7;
    border-radius: 4px;
    height: 84px;
    padding: 12px;
    margin: 24px 0;

    @include tablet {
      height: 104px;
      padding: 0px;
    }

    &__link {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
    }

    &__picture {
      flex-shrink: 0;
      width: 60px;
      height: 60px;
      margin-right: 12px;

      @include tablet {
        width: 80px;
        height: 80px;
        margin-right: 16px;
      }

      &-img {
        border-radius: 8px;
        width: 100%;
        height: 100%;
      }
    }

    &__body {
      display: flex;
      width: 100%;
      overflow: hidden;

      @include tablet {
        padding: 12px;
        padding-right: 0;
      }
    }

    &__app-name {
      @include normalText;
      font-family: $mainFont;
      font-weight: 500;
      overflow: hidden;
      display: block;
      text-overflow: ellipsis;
      white-space: nowrap;

      @include tablet {
        @include mediumText;
      }

      &-wrapper {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-bottom: 4px;

        @include tablet {
          margin-bottom: 8px;
        }
      }
    }

    &__reload-icon {
      display: none;

      @include desktop {
        display: block;
        width: 20px;
        height: 20px;
        margin-right: 12px;
        cursor: pointer;
        fill: #4c4c4c;
        flex-shrink: 0;
        z-index: 2;
      }

      @include hover {
        fill: black;
      }

      &--loading {
        animation: 1.5s mercury infinite ease;

        @keyframes mercury {
          0% {
            transform: rotate(0);
          }
          95% {
            transform: rotate(360deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      }
    }

    &__app-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      overflow: hidden;

      &-additional {
        @include smallText;
        font-weight: 700;
        color: #5c5c5c;
        display: flex;
        align-items: center;
      }
    }

    &__author-name {
      display: none;

      @include tablet {
        display: block;
        margin-right: 16px;
      }
    }

    &__store-icon {
      width: 24px;
      height: 24px;
      display: block;
      margin-right: 4px;

      @include tablet {
        display: none;
      }
    }

    &__arrow {
      :deep() {
        svg {
          width: 16px;
          height: 17px;
        }
      }
      margin-top: auto;
      pointer-events: none;

      @include tablet {
        display: none;
      }

      &-wrapper {
        margin-top: auto;
      }
    }

    &__button {
      display: none;
      text-decoration: none;

      @include tablet {
        display: block;
        height: 100%;
        line-height: 21px;
        text-align: center;
        background: var(--bg-color-substrate-2);
        color: #5c5c5c;
        cursor: pointer;
        z-index: 2;

        @include hover {
          color: black;
        }
      }

      &-text {
        @include smallText;
        font-weight: 700;
      }

      &-icon {
        margin-top: 5px;
        margin-right: -4px;
        width: 48px;
        height: 48px;
      }
    }
  }
</style>
